import { css, cx } from "@emotion/css";
import { borderRadius, borderWidth, colorScales, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import React, { useRef } from "react";
import { useIsHovered } from "../../hooks";
import { resetStyles } from "../../utils/resetStyles";
import { Tooltip } from "../Tooltip";
import { ProcessOverviewStep } from "./ProcessOverviewStep";
import type { ProcessOverviewStepContainerData } from "./types";
export interface ProcessOverviewStepContainerProps {
    stepContainerData: ProcessOverviewStepContainerData;
    showNote: boolean;
}
export function ProcessOverviewStepContainer({ stepContainerData, showNote }: ProcessOverviewStepContainerProps) {
    const { name, icon, iconTooltip, href, summary, isDisabled, containerType, containerSteps } = stepContainerData;
    const headerRef = useRef<HTMLAnchorElement>(null);
    const isHeaderHovered = useIsHovered(headerRef);
    const rootConditionalStyles = {
        [styles.disabled]: isDisabled,
        [styles.hover]: isHeaderHovered,
    };
    return (<div className={cx(styles.root, rootConditionalStyles)}>
            <a ref={headerRef} className={styles.stepHeader} href={href}>
                <Tooltip content={iconTooltip}>{"src" in icon ? <img className={cx(styles.icon, { [styles.disabled]: isDisabled })} src={icon.src} alt=""/> : icon}</Tooltip>
                <div className={styles.stepNameAndSummary}>
                    <div className={styles.name}>{name}</div>
                    {summary}
                </div>
                <div className={styles.stepTypeBadge}>{containerType}</div>
            </a>
            <ol className={styles.childSteps}>
                {containerSteps.map((stepData) => (<li key={stepData.id}>
                        <ProcessOverviewStep stepData={stepData} showNote={showNote}/>
                    </li>))}
            </ol>
        </div>);
}
const styles = {
    root: css({
        display: "flex",
        flex: 1,
        flexDirection: "column",
        gap: space[8],
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        borderRadius: borderRadius.small,
        backgroundColor: themeTokens.color.background.primary.default,
        boxShadow: themeTokens.shadow.extraSmall,
        font: text.body.regular.small,
        ":has(> a:first-child:focus)": {
            boxShadow: themeTokens.shadow.focused,
        },
    }),
    disabled: css({
        opacity: 0.5,
    }),
    hover: css({
        borderColor: colorScales.blue[300],
        backgroundColor: colorScales.blue[100],
    }),
    stepHeader: css({
        ...resetStyles.anchor,
        display: "flex",
        gap: space[8],
        padding: `${space[12]} ${space[12]} 0`,
    }),
    icon: css({
        display: "block",
        background: themeTokens.color.background.primary.default,
        borderRadius: borderRadius.extraSmall,
        height: "2.25rem",
        width: "2.25rem",
        objectFit: "scale-down",
    }),
    stepNameAndSummary: css({
        flex: 1,
    }),
    name: css({
        font: text.body.bold.medium,
    }),
    stepTypeBadge: css({
        display: "flex",
        alignSelf: "center",
        font: text.body.regular.xSmall,
        backgroundColor: themeTokens.color.chip.filled.background.primary,
        padding: `0 ${space[6]}`,
        height: "fit-content",
        textTransform: "capitalize",
        borderRadius: borderRadius.extraLarge,
    }),
    childSteps: css({
        ...resetStyles.list,
        display: "flex",
        flexDirection: "column",
        padding: `0 ${space[12]} ${space[12]}`,
        gap: space[8],
    }),
};
